import React, { useState, useEffect } from 'react';
import {
  LuMapPin,
  LuPhone,
  LuMail,
  LuBookOpen,
  LuSchool,
  LuUsers,
  LuLoader,
  LuSend,
  LuX,
  LuCheck,
  LuSparkles
} from 'react-icons/lu';
import { GiOpenBook } from 'react-icons/gi';
import { PiChurchLight } from 'react-icons/pi';
import publicationsHero from '../../assets/publication.png';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function OurPublications({ activeSection = 'publications', onSelectSection = () => { } }) {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubModal, setSelectedSubModal] = useState(null);
  const [subFormSubmitted, setSubFormSubmitted] = useState(false);
  const [subFormData, setSubFormData] = useState({ name: '', phone: '', address: '', plan: '1 Year' });

  // Fetch publications from Firestore
  useEffect(() => {
    let isMounted = true;
    async function fetchPublications() {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, 'publications'));
        const fetched = [];

        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const name = data.name || data.title || 'Diocesan Publication';
            const order = typeof data.order === 'number' ? data.order : 999;
            const imgUrl = data.img_url || data.image || data.img || '';
            const address = data.address || 'CSI Diocesan Office, Bank Road, Calicut - 673001';
            const phone = data.phone || data.contact || '0495 2721748';
            const email = data.email || 'csimalabardiocese@gmail.com';

            // Format Subscriptions from oneyear, fiveyear, twentyyear
            const subs = [];
            if (data.oneyear && data.oneyear !== '0000' && data.oneyear !== '0') {
              subs.push({ label: '1 Year Annual Plan', price: `₹${data.oneyear}` });
            }

            if (data.fiveyear && data.fiveyear !== '00000' && data.fiveyear !== '0') {
              subs.push({ label: '5 Years Regular Plan', price: `₹${data.fiveyear}` });
            }

            if (data.twentyyear && data.twentyyear !== '00000' && data.twentyyear !== '0') {
              subs.push({ label: '20 Years Life Subscription', price: `₹${data.twentyyear}` });
            }

            const isVanitha = name.toUpperCase().includes('VANITHA');

            fetched.push({
              id: doc.id,
              title: name.toUpperCase(),
              name: name.toUpperCase(),
              subtitle: isVanitha
                ? 'Diocesan Women’s Fellowship (Stree Samajam) Periodical'
                : 'Official Monthly Diocesan Magazine',
              description: data.description || (isVanitha
                ? 'A quarterly journal inspiring Christian women with devotional studies, homemaking, community health, and women’s leadership.'
                : 'The premier monthly magazine of the CSI Diocese of Malabar containing episcopal pastoral letters, theological articles, and parish news.'),
              address,
              phone,
              email,
              img_url: imgUrl,
              order,
              subs,
              raw: data
            });
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setPublications(fetched);
        }
      } catch (err) {
        console.error("Error fetching publications from Firebase:", err);
        if (isMounted) {
          setPublications([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchPublications();
    return () => { isMounted = false; };
  }, []);

  const handleSubSubmit = (e) => {
    e.preventDefault();
    if (!subFormData.name || !subFormData.phone) return;
    setSubFormSubmitted(true);
    setTimeout(() => {
      setSubFormSubmitted(false);
      setSelectedSubModal(null);
      setSubFormData({ name: '', phone: '', address: '', plan: '1 Year' });
    }, 2500);
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Hero Header Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={publicationsHero}
            alt="CSI Diocesan Publications"
            className="w-full h-full object-cover object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              PUBLICATIONS
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              Enriching spiritual lives with official diocesan periodicals, Christian literature, daily Scripture readings, and liturgical magazines.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">Publications</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuBookOpen className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">Sabhamithram</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Monthly Magazine</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <GiOpenBook className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">Vanithaninadham</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Women's Journal</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:mt-8 space-y-8 sm:space-y-10">

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading publications...</p>
          </div>
        ) : (
          /* Publications Cards Grid */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-stretch">
            {publications.map((pub, idx) => (
              <div
                key={idx}
                className="bg-white rounded-[24px] sm:rounded-[28px] border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_45px_rgba(132,35,67,0.09)] p-5 sm:p-8 flex flex-col justify-between transition-all group hover:-translate-y-1"
              >
                <div>
                  {/* Top Bar: Title & Cover Image/Icon */}
                  <div className="flex items-start justify-between gap-3 sm:gap-4 mb-4 sm:mb-5 border-b border-gray-100 pb-4 sm:pb-5">
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] sm:text-[10px] uppercase font-bold tracking-widest text-[#842343] bg-[#842343]/10 px-2.5 py-1 rounded-full inline-block mb-1.5 sm:mb-2">
                        Diocesan Publication
                      </span>
                      <h2 className="font-serif font-bold text-[#842343] text-xl sm:text-3xl leading-tight">
                        {pub.title}
                      </h2>
                      <p className="font-serif italic text-xs sm:text-sm text-[#5A4F4A] mt-1">
                        {pub.subtitle}
                      </p>
                    </div>

                    {pub.img_url ? (
                      <div className="w-16 h-24 sm:w-24 sm:h-32 rounded-xl overflow-hidden shadow-md border border-gray-200 shrink-0 bg-gray-50">
                        <img
                          src={pub.img_url}
                          alt={pub.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-center justify-center text-[#842343] shadow-xs shrink-0">
                        <LuBookOpen className="w-6 h-6 sm:w-7 sm:h-7" />
                      </div>
                    )}
                  </div>

                  <p className="text-xs sm:text-[14px] text-[#2D2420] leading-relaxed mb-5 sm:mb-6">
                    {pub.description}
                  </p>

                  {/* Split Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 bg-[#FAF7F2] p-4 sm:p-5 rounded-2xl border border-[#E8DDD7] mb-5 sm:mb-6">
                    {/* Editorial Desk Info */}
                    <div className="space-y-2.5 sm:space-y-3 text-xs text-[#5A4F4A]">
                      <span className="text-[10px] font-bold tracking-wider uppercase text-[#8A7E79] block">
                        Editorial Desk
                      </span>

                      <div className="flex items-start gap-2.5">
                        <LuMapPin className="w-4 h-4 text-[#842343] shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{pub.address}</span>
                      </div>

                      {pub.phone && (
                        <div className="flex items-center gap-2.5">
                          <LuPhone className="w-4 h-4 text-[#842343] shrink-0" />
                          <a href={`tel:${pub.phone}`} className="font-bold text-[#1a1715] hover:text-[#842343] transition-colors">
                            {pub.phone}
                          </a>
                        </div>
                      )}

                      {pub.email && (
                        <div className="flex items-center gap-2.5">
                          <LuMail className="w-4 h-4 text-[#842343] shrink-0" />
                          <a href={`mailto:${pub.email}`} className="text-[#842343] hover:underline font-semibold text-xs break-all">
                            {pub.email}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Subscription Pricing */}
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold tracking-wider uppercase text-[#8A7E79] block">
                        Subscription Plans
                      </span>
                      {pub.subs.map((s, sIdx) => (
                        <div key={sIdx} className="flex justify-between items-center bg-white p-2.5 rounded-xl text-xs border border-[#E8DDD7]/70 shadow-2xs">
                          <span className="text-[#5A4F4A] font-medium">{s.label}</span>
                          <strong className="text-[#842343] font-serif text-sm">{s.price}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="flex flex-col sm:flex-row items-center gap-2.5 sm:gap-3 pt-2">
                  <button
                    onClick={() => setSelectedSubModal(pub)}
                    className="w-full sm:flex-1 bg-[#842343] hover:bg-[#6c1b35] text-white font-sans font-bold text-xs py-3 px-4 rounded-xl shadow-xs transition-colors text-center cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>Subscribe to {pub.title}</span>
                    <LuSparkles className="w-3.5 h-3.5 text-[#D4A64A]" />
                  </button>

                  <a
                    href={`tel:${pub.phone}`}
                    className="w-full sm:w-auto bg-[#FAF7F2] hover:bg-[#EAE1DF] text-[#842343] border border-[#E8DDD7] text-xs font-bold py-3 px-5 rounded-xl transition-colors text-center flex items-center justify-center gap-1.5"
                  >
                    <LuPhone className="w-3.5 h-3.5" />
                    <span>Call Editorial</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Daily Readings Callout Banner */}
        <div className="bg-[#842343] rounded-[24px] sm:rounded-[28px] p-5 sm:p-8 text-white shadow-[0_8px_30px_rgba(132,35,67,0.2)] flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 relative overflow-hidden text-center sm:text-left">
          <div className="relative z-10 max-w-xl flex flex-col items-center sm:items-start">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#e5c98b] block mb-1">Scripture Meditation</span>
            <h3 className="font-serif font-bold text-xl sm:text-[24px] text-white mb-2 leading-tight">
              Daily Bible Readings &amp; Liturgy
            </h3>
            <p className="text-xs sm:text-sm text-white/90 leading-relaxed">
              Access the official CSI liturgical lectionary readings for personal devotions, morning prayer, and Sunday worship services.
            </p>
          </div>

          <button
            onClick={() => onSelectSection('readings')}
            className="relative z-10 w-full sm:w-auto bg-[#e5c98b] hover:bg-[#d8b874] text-[#1a1715] font-bold text-xs py-3 px-7 rounded-xl shadow-md transition-colors whitespace-nowrap cursor-pointer text-center"
          >
            Open Daily Readings 📖
          </button>
        </div>

      </div>

      {/* ── Subscribe Modal ── */}
      {selectedSubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => { setSelectedSubModal(null); setSubFormSubmitted(false); }}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 cursor-pointer"
            >
              <LuX className="w-4 h-4" />
            </button>

            <span className="text-xs font-bold uppercase tracking-widest text-[#842343] block mb-1">
              Subscription Desk
            </span>
            <h3 className="font-serif font-bold text-2xl text-[#1a1715] mb-2">
              Subscribe to {selectedSubModal.title}
            </h3>
            <p className="text-xs text-[#8A7E79] mb-5">
              Fill out your postal delivery details to receive your monthly edition at your doorstep.
            </p>

            {subFormSubmitted ? (
              <div className="bg-[#E9F9EE] border border-[#C5F0D2] p-6 rounded-2xl text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-[#25D366]/20 text-[#25D366] flex items-center justify-center mx-auto">
                  <LuCheck className="w-6 h-6" />
                </div>
                <h4 className="font-serif font-bold text-[#1a1715] text-lg">Subscription Requested</h4>
                <p className="text-xs text-[#5A4F4A]">
                  Thank you, <strong>{subFormData.name}</strong>. Our editorial team will contact you at <strong>{subFormData.phone}</strong> with payment &amp; dispatch confirmation.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#5A4F4A] mb-1">Select Subscription Plan *</label>
                  <select
                    value={subFormData.plan}
                    onChange={(e) => setSubFormData({ ...subFormData, plan: e.target.value })}
                    className="w-full bg-[#FAF7F2] border border-[#E8DDD7] rounded-xl px-3.5 py-2.5 text-xs text-[#1a1715] outline-none focus:border-[#842343] cursor-pointer"
                  >
                    {selectedSubModal.subs.map((s, idx) => (
                      <option key={idx} value={s.label}>
                        {s.label} — {s.price}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#5A4F4A] mb-1">Subscriber Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter subscriber name"
                    value={subFormData.name}
                    onChange={(e) => setSubFormData({ ...subFormData, name: e.target.value })}
                    className="w-full bg-[#FAF7F2] border border-[#E8DDD7] rounded-xl px-3.5 py-2.5 text-xs text-[#1a1715] outline-none focus:border-[#842343]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#5A4F4A] mb-1">Contact Phone Number *</label>
                  <input
                    type="tel"
                    required
                    placeholder="Enter mobile number"
                    value={subFormData.phone}
                    onChange={(e) => setSubFormData({ ...subFormData, phone: e.target.value })}
                    className="w-full bg-[#FAF7F2] border border-[#E8DDD7] rounded-xl px-3.5 py-2.5 text-xs text-[#1a1715] outline-none focus:border-[#842343]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#5A4F4A] mb-1">Postal Delivery Address (Optional)</label>
                  <textarea
                    rows={3}
                    placeholder="House name, street, post office, pin code..."
                    value={subFormData.address}
                    onChange={(e) => setSubFormData({ ...subFormData, address: e.target.value })}
                    className="w-full bg-[#FAF7F2] border border-[#E8DDD7] rounded-xl p-3.5 text-xs text-[#1a1715] outline-none focus:border-[#842343] resize-none"
                  ></textarea>
                </div>

                <div className="pt-2 flex items-center justify-between gap-3 border-t border-gray-100">
                  <a
                    href={`tel:${selectedSubModal.phone}`}
                    className="text-xs text-[#842343] font-semibold hover:underline flex items-center gap-1"
                  >
                    <LuPhone className="w-3 h-3" />
                    <span>Call: {selectedSubModal.phone}</span>
                  </a>

                  <button
                    type="submit"
                    className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-bold py-2.5 px-6 rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <span>Confirm Request</span>
                    <LuSend className="w-3.5 h-3.5" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── 2. Bottom Statistics Strip ── */}
      <section className="mt-14 pt-8 pb-8 bg-[#f5ede5]/70 border-t border-[#ede2d6]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <div className="flex items-center justify-center gap-3">
              <PiChurchLight className="w-7 h-7 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">26+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Dioceses</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuBookOpen className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">5</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuSchool className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">120+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Institutions</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">49</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Presbyters</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">4.5M+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Members</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span className="text-[#842343] text-xl font-serif">✝</span>
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">100+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Worship Services</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
